import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useLanguage } from '../../context/LanguageContext';
import { i18n } from '../../i18n/i18n';
import { Task } from 'shared/types';
import AntMascot from '../AntMascot';
import TaskMemberModal from '../TaskMemberModal';
import { KidButton, KidCard, KidSectionHeading, PictureTile } from '../kid/KidUI';
import { KidIcon, Star } from '../kid/KidIcon';
import { KidTaskCard } from '../kid/KidTaskCard';
import { pictureTint, TINT_CLASS } from '../kid/kidUiHelpers';
import { isGlobalCatalogAdmin } from '../../utils/catalogAdmin';
import { getRoom, getRoomName } from '../../utils/roomName';
import { pluralKey } from '../../utils/starGoal';
import HandOverRewards from './HandOverRewards';
import { PocketMoneyToPay } from '../pocket/PocketMoney';
import { GettingStartedCard, PocketMoneyInvite } from './GettingStarted';
import { isAvailableNow } from '../../utils/taskAvailability';
import { avatarOrDefault } from '../../utils/avatars';
import { resolveAssetUrl } from '../../services/apiService';

/**
 * A grown-up's Home (kid-first redesign step 6 — mock-ups phone-13 / tablet-*-10): the
 * approvals card where the banner was, the family without ranks, and shortcuts into the admin
 * sections. A parent's own tasks (if any) follow below.
 */

export const APPROVE_PATH = '/approve';

const initialCircle = (name: string, size = 'w-9 h-9 text-base') => (
  <span aria-hidden className={`${size} shrink-0 rounded-full flex items-center justify-center font-kid-heading font-extrabold text-kid-text ${TINT_CLASS[pictureTint(name)]}`}>
    {name.trim().charAt(0).toLocaleUpperCase()}
  </span>
);

const ParentHome: React.FC = () => {
  const { current_user, current_family, refreshFamilyData, switchUser } = useAppContext() as any;
  const { current_language } = useLanguage();
  const navigate = useNavigate();
  const [taskModalTask, setTaskModalTask] = useState<Task | null>(null);

  if (!current_user || !current_family) return null;

  const tasks = current_family.tasks as any[];
  const pending = tasks
    .filter((t) => t.is_completed && !t.verified)
    .sort((a, b) => new Date(a.completed_at || 0).getTime() - new Date(b.completed_at || 0).getTime());
  const nameOf = (id: string) => current_family.members.find((m: any) => m.id === id)?.name || '';
  const members = [...current_family.members].sort((a: any, b: any) => {
    if (a.member_type !== b.member_type) return a.member_type === 'child' ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  const openCount = (id: string) => tasks.filter((t) => t.assigned_to === id && !t.is_completed && isAvailableNow(t)).length;
  const myTasks = tasks.filter((t) => t.assigned_to === current_user.id && !t.is_completed && isAvailableNow(t));

  const shortcut = (to: string, icon: React.ReactNode, label: string) => (
    <Link
      to={to}
      className="min-h-[64px] flex items-center gap-3 px-4 rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark font-bold text-lg"
    >
      {icon}
      <span className="leading-tight">{label}</span>
    </Link>
  );

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 md:px-8 py-4 md:py-7 text-kid-text dark:text-kid-text-dark" lang={current_language}>
      {isGlobalCatalogAdmin(current_user) && (
        <KidCard tone="peach" className="mb-5 flex flex-wrap items-center justify-between gap-3 !py-3">
          <span className="text-base font-bold">Master admin: standard tools and standard tasks for all families.</span>
          <Link to="/global-admin/tools" className="min-h-[44px] inline-flex items-center rounded-full px-5 bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong font-bold">
            Open master admin
          </Link>
        </KidCard>
      )}

      <div className="flex items-center justify-between gap-3 mb-5">
        <h1 className="font-kid-heading font-extrabold text-3xl md:text-4xl leading-tight">
          {i18n.t('dashboard.hi_name').replace('{{name}}', current_user.name)}
        </h1>
        <div className="flex items-center gap-2 shrink-0">
          <Link to="/settings" aria-label={i18n.t('parent.settings')} title={i18n.t('parent.settings')}
            className="w-12 h-12 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark flex items-center justify-center">
            <KidIcon name="settings" size={26} />
          </Link>
          <KidButton size="md" variant="secondary" onClick={() => switchUser()} icon={<KidIcon name="lock" size={20} />}>
            {i18n.t('parent.lock')}
          </KidButton>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-8 lg:items-start">
        <div className="space-y-6">
          {/* approvals */}
          {pending.length > 0 ? (
            <KidCard tone="peach" className="!border-2 !border-kid-orange">
              <h2 className="font-kid-heading font-extrabold text-2xl md:text-3xl mb-3">
                {i18n.t(pluralKey('parent.to_approve', pending.length)).replace('{{n}}', String(pending.length))}
              </h2>
              <ul className="space-y-2 mb-4">
                {pending.slice(0, 4).map((t) => (
                  <li key={t.id} className="flex items-center gap-3">
                    {initialCircle(nameOf(t.completed_by))}
                    <span className="font-bold text-base md:text-lg leading-tight">
                      {i18n.t('parent.finished').replace('{{name}}', nameOf(t.completed_by)).replace('{{task}}', t.title)}
                    </span>
                  </li>
                ))}
                {pending.length > 4 && (
                  <li className="font-bold pl-12">{i18n.t('parent.more_waiting').replace('{{n}}', String(pending.length - 4))}</li>
                )}
              </ul>
              <KidButton size="lg" fullWidth onClick={() => navigate(APPROVE_PATH)} trailing={<span aria-hidden>→</span>}>
                {i18n.t('parent.review')}
              </KidButton>
            </KidCard>
          ) : (
            <KidCard tone="mint" className="flex items-center gap-4">
              <AntMascot pose="wave" className="w-14 h-14 shrink-0" />
              <p className="font-bold text-lg">{i18n.t('parent.nothing_to_approve')}</p>
            </KidCard>
          )}

          <HandOverRewards />
          <PocketMoneyToPay />
          {/* After setup (2026-10-15): small first steps, and pocket money a week in */}
          <GettingStartedCard family={current_family} />
          <PocketMoneyInvite family={current_family} />

          {/* the parent's own tasks, if any */}
          {myTasks.length > 0 && (
            <section className="space-y-3">
              <KidSectionHeading>{i18n.t('parent.your_tasks')}</KidSectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {myTasks.map((t) => (
                  <KidTaskCard
                    key={t.id}
                    task={t}
                    roomName={getRoomName(current_family.rooms, t.room_id)}
                    roomImage={getRoom(current_family.rooms, t.room_id)?.image_url}
                    actionLabel={i18n.t('task_flow.start_task')}
                    actionTrailing={<span aria-hidden>→</span>}
                    onAction={() => setTaskModalTask(t as Task)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* set up */}
          <section className="space-y-3">
            <KidSectionHeading>{i18n.t('parent.set_up')}</KidSectionHeading>
            <div className="grid grid-cols-2 gap-3">
              {shortcut('/admin?tab=tasks', <KidIcon name="tasks" size={26} />, i18n.t('parent.tasks'))}
              {shortcut('/admin?tab=rewards', <KidIcon name="rewards" size={26} />, i18n.t('parent.rewards'))}
              {shortcut('/admin?tab=members', <span className="text-2xl" aria-hidden>👥</span>, i18n.t('parent.members'))}
              {shortcut('/admin?tab=rooms', <KidIcon name="home" size={26} />, i18n.t('parent.rooms_tools'))}
            </div>
          </section>
        </div>

        {/* family, no ranks */}
        <section className="space-y-3">
          <KidSectionHeading>{i18n.t('parent.family')}</KidSectionHeading>
          <KidCard className="!p-2 md:!p-3">
            <ul>
              {members.map((m: any) => {
                const n = openCount(m.id);
                return (
                  <li key={m.id} className="flex items-center gap-3 px-2 py-2.5">
                    <PictureTile src={resolveAssetUrl(avatarOrDefault(m.avatar_url))} label={m.name} size={36} rounded="rounded-full" />
                    <span className="flex-1 min-w-0 font-bold text-lg truncate">{m.name}</span>
                    <span className="text-sm md:text-base shrink-0">
                      {n === 0 ? i18n.t('parent.open_none') : i18n.t(pluralKey('parent.open', n)).replace('{{n}}', String(n))}
                    </span>
                    <span className="inline-flex items-center gap-1 font-kid-heading font-extrabold text-lg w-16 justify-end shrink-0">
                      <Star size={18} /> {m.points}
                    </span>
                  </li>
                );
              })}
            </ul>
          </KidCard>
        </section>
      </div>

      <TaskMemberModal
        task={taskModalTask}
        open={!!taskModalTask}
        startInFlow
        onClose={() => {
          setTaskModalTask(null);
          void refreshFamilyData();
        }}
      />
    </div>
  );
};

export default ParentHome;
